<template>
<div class="dnp-page">
  <div class="dnp-toolbar no-print">
    <button class="btn btn-sm btn-success" @click="doPrint"><i class="mdi mdi-printer me-1"></i>Print / Save as PDF</button>
    <doc-share v-if="dn && !sharedView" class="ms-2" :title="'Delivery Note — ' + (dn.farm_name || '')" :doc-ref="dn.document_ref" doc-type="delivery_note" :doc-id="dn.id" />
    <button class="btn btn-sm btn-outline-secondary ms-2" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
  </div>

  <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

  <div v-else-if="dn" class="dnp-sheet">
    <!-- Header -->
    <header class="dnp-header">
      <div>
        <h1 class="dnp-ranch-name">{{ (dn.farm_name || '').toUpperCase() }}</h1>
        <p class="dnp-ranch-addr">
          <span v-if="dn.farm_address">{{ dn.farm_address }}</span>
          <span v-if="dn.farm_county"> — {{ dn.farm_county }} County, Kenya</span>
        </p>
        <p v-if="dn.farm_phone" class="dnp-ranch-addr">Tel: {{ dn.farm_phone }}</p>
      </div>
      <div class="dnp-title-box">
        <div class="dnp-title">DELIVERY NOTE</div>
        <div class="dnp-ref">{{ dn.document_ref }}</div>
      </div>
    </header>

    <!-- Summary strip -->
    <div class="dnp-strip">
      <div><span class="dnp-label">DELIVERY DATE</span><strong>{{ fmtDate(dn.delivery_date) }}</strong></div>
      <div><span class="dnp-label">DESTINATION</span><strong>{{ dn.destination_name }}</strong></div>
      <div><span class="dnp-label">TOTAL ANIMALS</span><strong>{{ dn.total_animals }} {{ animalNoun }}</strong></div>
    </div>

    <div class="dnp-banner">
      ▸ All animals delivered with ear tags | Inspection required on receipt | Record any discrepancies before signing
    </div>

    <!-- Animal cards -->
    <h3 class="dnp-section-title">ANIMAL REGISTER — INDIVIDUAL CONDITION RECORD</h3>
    <div class="dnp-grid">
      <div v-for="(it, i) in dn.items" :key="'it-'+it.id" class="dnp-card">
        <div class="dnp-card-head">
          <span class="dnp-card-tag">{{ it.ear_tag }}</span>
          <span class="dnp-card-num">#{{ i+1 }} of {{ dn.items.length }}</span>
        </div>
        <table class="dnp-table">
          <tr><td>EAR TAG</td><td>{{ it.ear_tag }}</td></tr>
          <tr><td>CLASS</td><td>{{ it.animal_class || '—' }}</td></tr>
          <tr><td>EST. LIVEWEIGHT</td><td>{{ it.est_liveweight_kg ? Number(it.est_liveweight_kg).toFixed(0) + ' kg' : '— kg' }}</td></tr>
          <tr><td>BREED TYPE</td><td>{{ it.breed_type || '—' }}</td></tr>
          <tr><td>CONDITION ON DISPATCH</td><td><span class="dnp-pill" :class="'p-'+it.dispatch_condition">{{ (it.dispatch_condition||'').toUpperCase() }}</span></td></tr>
          <tr><td>CONDITION ON RECEIPT</td><td><span class="dnp-pill" :class="'p-'+(it.receipt_condition||'pending')">{{ (it.receipt_condition||'pending').toUpperCase() }}</span></td></tr>
        </table>
        <div class="dnp-remarks-label">REMARKS</div>
        <div class="dnp-remarks">{{ it.remarks || '—' }}</div>
      </div>
    </div>

    <!-- Photos -->
    <div v-if="(dn.photos||[]).length" class="dnp-photos-section">
      <h3 class="dnp-section-title">HANDLING &amp; LOADING — PHOTOGRAPHIC RECORD</h3>
      <div class="dnp-photos">
        <div v-for="p in dn.photos" :key="'ph-'+p.id" class="dnp-photo">
          <img :src="imgUrl(p.photo_url)" />
          <div class="dnp-photo-cap">{{ p.caption || '' }}</div>
        </div>
      </div>
    </div>

    <!-- Checklist -->
    <h3 class="dnp-section-title">DISPATCH CONDITION CHECKLIST</h3>
    <div class="dnp-checklist">
      <div v-for="c in checklistKeys" :key="c.key" class="dnp-chk">
        <span class="dnp-chk-mark">{{ checklist[c.key] ? '☑' : '☐' }}</span> {{ c.label }}
      </div>
    </div>

    <!-- Signatures -->
    <h3 class="dnp-section-title">AUTHORISATIONS &amp; ACKNOWLEDGEMENTS</h3>
    <div class="dnp-sign-grid">
      <div>
        <div class="dnp-sign-role">PREPARED BY (RANCH)</div>
        <div class="dnp-sign-line">Name: {{ dn.prepared_by_name || '___________________' }}</div>
        <div class="dnp-sign-line">Date: {{ fmtDate(dn.delivery_date) }}</div>
      </div>
      <div>
        <div class="dnp-sign-role">DRIVER / TRANSPORTER</div>
        <div class="dnp-sign-line">Name: {{ dn.driver_name || '___________________' }}</div>
        <div class="dnp-sign-line">Vehicle Reg: {{ dn.vehicle_reg || '_______________' }}</div>
      </div>
      <div>
        <div class="dnp-sign-role">RECEIVED BY ({{ (dn.destination_name||'').toUpperCase() }})</div>
        <div class="dnp-sign-line">Name: {{ dn.received_by_name || '___________________' }}</div>
        <div class="dnp-sign-line">Date / Time: {{ dn.received_at ? fmtDateTime(dn.received_at) : '______________' }}</div>
      </div>
    </div>

    <!-- Vet declaration -->
    <div class="dnp-vet">
      <strong>VETERINARY DECLARATION</strong> | I confirm that the animals described herein have been inspected and are, to the best of my knowledge, free from notifiable disease, fit for transport, and accompanied by a valid movement permit issued by the Directorate of Veterinary Services.
      <div class="dnp-vet-grid">
        <div>Vet Officer Name: <strong>{{ dn.vet_officer_name || '______________________' }}</strong></div>
        <div>DVS Permit No: <strong>{{ dn.vet_dvs_permit_no || dn.dvs_permit_no || '______________________' }}</strong></div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="dnp-footer">
      <span>{{ dn.document_ref }} · {{ dn.farm_county || '' }} County · {{ fmtDate(dn.delivery_date) }}</span>
      <span class="dnp-footer-count">{{ dn.total_animals }} {{ animalNoun.toUpperCase() }}</span>
      <span>Thank you for your business</span>
    </footer>
  </div>
</div>
</template>

<style>
/* Match the Cape Chestnut document aesthetic — sand/burnt-orange palette, monospaced detail rows */
.dnp-page { background:#F1EBDE; min-height:100vh; padding:30px 16px; color:#1A1A1A; font-family:'Helvetica Neue',Arial,sans-serif; }
.dnp-toolbar { max-width:1000px; margin:0 auto 16px; display:flex; justify-content:center; }
.dnp-sheet { max-width:1000px; margin:0 auto; background:#F5EEDE; border:1px solid #D8C9A3; padding:30px; box-shadow:0 4px 24px rgba(0,0,0,.08); }

.dnp-header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:18px; border-bottom:3px double #8B5A2B; margin-bottom:18px; }
.dnp-ranch-name { font-family:Georgia,serif; color:#8B5A2B; font-size:24px; letter-spacing:1.5px; font-weight:700; margin:0; }
.dnp-ranch-addr { font-family:'Courier New',monospace; color:#5C4533; font-size:11px; margin:4px 0 0; line-height:1.5; }
.dnp-title-box { text-align:right; }
.dnp-title { font-family:Georgia,serif; color:#8B5A2B; font-size:24px; letter-spacing:3px; font-weight:700; }
.dnp-ref { font-family:'Courier New',monospace; color:#8B5A2B; font-size:13px; letter-spacing:1.5px; }

.dnp-strip { display:grid; grid-template-columns:1fr 2fr 1fr; gap:0; background:#8B3F1E; color:#F5EEDE; padding:14px 20px; border-radius:2px; margin-bottom:6px; }
.dnp-strip > div { display:flex; flex-direction:column; }
.dnp-strip .dnp-label { font-size:9px; letter-spacing:1.5px; opacity:.75; margin-bottom:3px; }
.dnp-strip strong { font-family:Georgia,serif; font-size:16px; }

.dnp-banner { background:#E8DFC5; padding:8px 14px; font-size:11px; color:#5C4533; text-align:center; margin-bottom:24px; border-left:3px solid #8B5A2B; }

.dnp-section-title { font-family:Georgia,serif; color:#5C4533; font-size:12px; letter-spacing:2px; text-align:center; margin:28px 0 14px; padding-bottom:6px; border-bottom:1px solid #D8C9A3; }

.dnp-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:14px; }
.dnp-card { background:#FBF6E8; border:1px solid #D8C9A3; border-radius:4px; overflow:hidden; display:flex; flex-direction:column; }
.dnp-card-head { background:#8B3F1E; color:#F5EEDE; display:flex; justify-content:space-between; padding:6px 12px; font-size:12px; font-family:Georgia,serif; letter-spacing:1px; }
.dnp-card-tag { font-weight:700; }
.dnp-card-num { font-size:10px; opacity:.85; }
.dnp-table { width:100%; font-family:'Courier New',monospace; font-size:11px; }
.dnp-table td { padding:5px 12px; border-bottom:1px dotted #D8C9A3; color:#5C4533; }
.dnp-table td:first-child { color:#8B5A2B; letter-spacing:.5px; }
.dnp-table td:last-child { text-align:right; font-weight:700; color:#1A1A1A; }
.dnp-table tr:last-child td { border-bottom:none; }
.dnp-pill { padding:1px 7px; border-radius:3px; font-size:9px; letter-spacing:1px; font-weight:700; }
.dnp-pill.p-good { background:#C8E6C9; color:#1F6B1F; }
.dnp-pill.p-fair { background:#FFE0B2; color:#8B5A2B; }
.dnp-pill.p-poor { background:#FFCDD2; color:#B71C1C; }
.dnp-pill.p-pending { background:#E0DDD3; color:#777; }
.dnp-remarks-label { font-family:'Courier New',monospace; font-size:9px; color:#8B5A2B; letter-spacing:1px; padding:6px 12px 2px; }
.dnp-remarks { font-family:'Courier New',monospace; font-size:11px; color:#5C4533; font-style:italic; padding:0 12px 10px; min-height:24px; }

.dnp-photos { display:grid; grid-template-columns:repeat(4, 1fr); gap:10px; }
.dnp-photo { background:#FBF6E8; border:1px solid #D8C9A3; border-radius:3px; overflow:hidden; }
.dnp-photo img { width:100%; height:110px; object-fit:cover; display:block; }
.dnp-photo-cap { font-family:'Courier New',monospace; font-size:9px; color:#5C4533; padding:4px 6px; text-align:center; }

.dnp-checklist { display:grid; grid-template-columns:1fr 1fr; gap:6px 24px; padding:6px 0; }
.dnp-chk { font-size:11px; color:#1A1A1A; font-family:'Courier New',monospace; }
.dnp-chk-mark { font-size:13px; }

.dnp-sign-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:24px; padding:6px 0 12px; }
.dnp-sign-role { font-family:'Courier New',monospace; font-size:10px; color:#8B5A2B; letter-spacing:1.2px; margin-bottom:8px; }
.dnp-sign-line { font-family:'Courier New',monospace; font-size:11px; color:#1A1A1A; margin-bottom:5px; }

.dnp-vet { background:#FBF6E8; border:1px solid #D8C9A3; border-radius:3px; padding:12px 14px; font-family:'Courier New',monospace; font-size:11px; color:#5C4533; line-height:1.6; margin-top:14px; }
.dnp-vet-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-top:8px; }

.dnp-footer { display:flex; justify-content:space-between; align-items:center; background:#1A1A1A; color:#F5EEDE; padding:10px 20px; font-family:'Courier New',monospace; font-size:11px; margin-top:24px; }
.dnp-footer-count { background:#8B3F1E; padding:3px 12px; border-radius:2px; font-size:11px; font-weight:700; letter-spacing:1.5px; }

@media print {
  .no-print { display:none !important; }
  .dnp-page { background:#fff; padding:0; }
  .dnp-sheet { box-shadow:none; border:none; max-width:none; }
}
@media (max-width: 900px) {
  .dnp-grid { grid-template-columns:repeat(2, 1fr); }
  .dnp-photos { grid-template-columns:repeat(2, 1fr); }
  .dnp-sign-grid { grid-template-columns:1fr; }
}

/* On-screen mobile preview */
@media (max-width: 768px) {
  .dnp-page { padding: 12px; }
  .dnp-sheet { padding: 16px; border-radius: 6px; }
  .dnp-sheet h1, .dnp-sheet h2 { font-size: 16px; }
  .dnp-grid { grid-template-columns: 1fr; }
  .dnp-photos { grid-template-columns: repeat(2, 1fr); gap: 6px; }
  .dnp-table { font-size: 10px; }
  .dnp-table td, .dnp-table th { padding: 3px 5px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";
import DocShare from "@/components/flockr-farm/DocShare.vue";
export default {
  name: "DeliveryNotePrint",
  components: { DocShare },
  data(){ return { loading:true, dn:null, checklist:{} }; },
  computed:{
    checklistKeys(){
      return [
        {key:'tags_match', label:'All animals ear-tagged and matching delivery note'},
        {key:'no_wounds', label:'Animals free from visible wounds or lesions'},
        {key:'permit', label:'Movement permit attached (DVS)'},
        {key:'no_lameness', label:'Animals free from lameness'},
        {key:'vet_clearance', label:'County veterinary inspection clearance obtained'},
        {key:'not_pregnant', label:'Animals not pregnant / in milk (or N/A)'},
        {key:'watered_rested', label:'Animals watered and rested before loading'},
        {key:'vehicle_ok', label:'Vehicle condition inspected and approved'},
        {key:'halal', label:'Halal handling requirements observed (if applicable)'},
        {key:'cold_chain', label:'Cold chain / lairage arrangements confirmed at destination'}
      ];
    },
    animalNoun(){
      if (!this.dn || !this.dn.items || !this.dn.items.length) return 'animals';
      var classes = {};
      this.dn.items.forEach(function(it){ var c = (it.animal_class||'').toLowerCase(); if (c) classes[c] = (classes[c]||0)+1; });
      var keys = Object.keys(classes);
      if (keys.length === 1) {
        var cls = keys[0];
        return cls.charAt(0).toUpperCase() + cls.slice(1) + (this.dn.total_animals > 1 ? 's' : '');
      }
      return 'animals';
    },
    sharedView(){ return !!this.$route.query.t; }
  },
  mounted(){
    var id = this.$route.params.id;
    var token = this.$route.query.t;
    if (token) this.loadByToken(token);
    else this.load(id);
  },
  methods:{
    imgUrl(u){if(!u)return'';if(String(u).indexOf('http')===0)return u;return uploads+String(u).replace(/^uploads\//,'');},
    fmtDate(d){ if(!d)return ''; try { var x = new Date(d); var p = function(n){return n<10?'0'+n:n}; return p(x.getDate())+' / '+p(x.getMonth()+1)+' / '+x.getFullYear(); } catch(e){ return d; } },
    fmtDateTime(d){ if(!d)return ''; try { return new Date(d).toLocaleString(); } catch(e){ return d; } },
    async load(id){
      this.loading = true;
      try {
        var r = await executeJson({ function:'getDeliveryNote', deliveryNoteId:id }, flocker);
        if (r.data && r.data.success) {
          this.dn = r.data.data.deliveryNote;
          try { this.checklist = this.dn.checklist ? JSON.parse(this.dn.checklist) : {}; } catch(e){ this.checklist = {}; }
        }
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    async loadByToken(token){
      this.loading = true;
      try {
        var r = await executeJson({ function:'getSharedDocument', token: token }, flocker);
        if (r.data && r.data.success) {
          this.dn = r.data.data.deliveryNote;
          try { this.checklist = this.dn && this.dn.checklist ? JSON.parse(this.dn.checklist) : {}; } catch(e){ this.checklist = {}; }
        } else {
          alert((r.data && r.data.message) || 'This share link is no longer valid.');
        }
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    doPrint(){ window.print(); }
  }
};
</script>
